import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from '../hooks/use-toast';
import { ArrowLeft, Check, FileText } from 'lucide-react';
import api from '../api/axios';

const TEMPLATES = [
  {
    id: 'classique',
    name: 'Classique',
    desc: 'Sobre et professionnel, bordeaux sur fond clair',
    colors: { banner: '#8B0000', accent: '#2c1810', bg: '#f5f1ed', row: '#fcfaf8', text: '#282828' },
  },
  {
    id: 'moderne',
    name: 'Moderne',
    desc: 'Minimaliste, noir et blanc epure',
    colors: { banner: '#1e1e1e', accent: '#505050', bg: '#f5f5f5', row: '#fafafa', text: '#141414' },
  },
  {
    id: 'elegance',
    name: 'Elegance',
    desc: 'Serif dore sur fond creme chaud',
    colors: { banner: '#8B5A2B', accent: '#5A3C1E', bg: '#FFFAF0', row: '#FFFCF5', text: '#32281E' },
  },
  {
    id: 'bistro',
    name: 'Bistro',
    desc: 'Style ardoise, vert olive rustique',
    colors: { banner: '#3C463C', accent: '#28322B', bg: '#F0EEE6', row: '#F5F3EE', text: '#232823' },
  },
  {
    id: 'luxe',
    name: 'Luxe',
    desc: 'Noir profond avec accents or',
    colors: { banner: '#141414', accent: '#B49632', bg: '#FAF8F0', row: '#F8F6F0', text: '#1E1E1E' },
  },
  {
    id: 'tropical',
    name: 'Tropical',
    desc: 'Couleurs chaudes de la Guyane',
    colors: { banner: '#C8501E', accent: '#963C14', bg: '#FFF8F0', row: '#FFFAF2', text: '#322314' },
  },
  {
    id: 'corporate',
    name: 'Corporate',
    desc: 'Strict et professionnel, bleu marine',
    colors: { banner: '#32415A', accent: '#46556E', bg: '#F0F3F8', row: '#F5F7FA', text: '#282D37' },
  },
  {
    id: 'vintage',
    name: 'Vintage',
    desc: 'Retro, typographie brun ancien',
    colors: { banner: '#784632', accent: '#5A3223', bg: '#F8F2E6', row: '#FAF5EC', text: '#3C281E' },
  },
  {
    id: 'fresh',
    name: 'Fresh',
    desc: 'Vert nature, clean et moderne',
    colors: { banner: '#287846', accent: '#1E5A37', bg: '#F0FAF4', row: '#F4FCF6', text: '#1E3223' },
  },
  {
    id: 'royal',
    name: 'Royal',
    desc: 'Pourpre regal et noble',
    colors: { banner: '#501464', accent: '#3C0F4B', bg: '#F8F2FC', row: '#FAF5FD', text: '#2D1937' },
  },
];

const SAMPLE_ITEMS = [
  { name: 'Brochette Poulet x2', qty: 2, price: 6.90 },
  { name: 'Colombo de Poulet', qty: 1, price: 13.50 },
  { name: 'Coca-Cola', qty: 2, price: 2.50 },
];

const SAMPLE_PAYMENTS = [
  { method: 'CB', amount: 18.40, ref: 'FAC-20260404-0012' },
  { method: 'Espece', amount: 11.90, ref: 'FAC-20260404-0013' },
];

const TemplatePreview = ({ tpl, isActive, onSelect }) => {
  const c = tpl.colors;
  const total = SAMPLE_ITEMS.reduce((s, i) => s + i.qty * i.price, 0);

  return (
    <Card
      className={`cursor-pointer transition-all duration-200 overflow-hidden ${
        isActive
          ? 'ring-2 ring-offset-2 shadow-lg scale-[1.02]'
          : 'hover:shadow-md hover:scale-[1.01] border-gray-200'
      }`}
      style={isActive ? { ringColor: c.banner, borderColor: c.banner } : {}}
      onClick={onSelect}
      data-testid={`template-${tpl.id}`}
    >
      {/* Mini banner */}
      <div className="relative h-14 flex items-center px-4 gap-3" style={{ backgroundColor: c.banner }}>
        <img src="/logo-taverne.png" alt="" className="h-9 rounded" />
        <div>
          <p className="text-white font-bold text-sm tracking-wide">LA TAVERNE</p>
          <p className="text-[10px]" style={{ color: 'rgba(255,255,255,0.6)' }}>Restaurant & Grillades</p>
        </div>
        {isActive && (
          <div className="absolute top-2 right-2 bg-white rounded-full p-0.5">
            <Check size={14} style={{ color: c.banner }} strokeWidth={3} />
          </div>
        )}
      </div>

      {/* Mini invoice body */}
      <div className="p-3" style={{ backgroundColor: c.bg }}>
        <div className="flex justify-between items-start mb-2">
          <div>
            <p className="font-bold text-xs" style={{ color: c.accent }}>FACTURE</p>
            <p className="text-[9px]" style={{ color: c.text }}>FAC-20260404-0012</p>
          </div>
          <div className="text-right px-2 py-1 rounded text-[9px]" style={{ backgroundColor: c.row, border: `1px solid ${c.banner}30` }}>
            <p style={{ color: c.text }} className="font-semibold">Jean Dupont</p>
          </div>
        </div>

        {/* Mini table */}
        <div className="rounded overflow-hidden mb-2" style={{ border: `1px solid ${c.banner}20` }}>
          <div className="flex text-[8px] font-bold text-white px-2 py-1" style={{ backgroundColor: c.banner }}>
            <span className="flex-1">Article</span>
            <span className="w-8 text-center">Qte</span>
            <span className="w-14 text-right">Total</span>
          </div>
          {SAMPLE_ITEMS.map((item, i) => (
            <div
              key={i}
              className="flex text-[8px] px-2 py-0.5"
              style={{ backgroundColor: i % 2 ? c.row : 'transparent', color: c.text }}
            >
              <span className="flex-1 truncate">{item.name}</span>
              <span className="w-8 text-center">{item.qty}</span>
              <span className="w-14 text-right font-semibold">{(item.qty * item.price).toFixed(2)}</span>
            </div>
          ))}
        </div>

        {/* Total */}
        <div className="flex justify-end mb-2">
          <div className="px-3 py-1 rounded text-[10px] font-bold" style={{ backgroundColor: c.row, border: `1px solid ${c.banner}40`, color: c.banner }}>
            TOTAL: {total.toFixed(2)} EUR
          </div>
        </div>

        {/* Payments */}
        <p className="text-[8px] font-bold mb-0.5" style={{ color: c.accent }}>Paiements</p>
        <div className="space-y-0.5">
          {SAMPLE_PAYMENTS.map((p, i) => (
            <div key={i} className="flex text-[8px] px-1" style={{ color: c.text }}>
              <span className="flex-1">{p.method}</span>
              <span className="font-semibold">{p.amount.toFixed(2)} EUR</span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer label */}
      <CardContent className="p-3 border-t flex items-center justify-between" style={{ borderColor: `${c.banner}20` }}>
        <div>
          <p className="font-bold text-sm" style={{ color: c.accent }}>{tpl.name}</p>
          <p className="text-[10px] text-gray-500">{tpl.desc}</p>
        </div>
        {isActive && (
          <Badge className="text-white text-[10px]" style={{ backgroundColor: c.banner }}>
            Actif
          </Badge>
        )}
      </CardContent>
    </Card>
  );
};

const InvoiceTemplates = () => {
  const navigate = useNavigate();
  const [activeTemplate, setActiveTemplate] = useState('classique');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('staffToken');
    const role = localStorage.getItem('staffRole');
    if (!token) { navigate('/staff/login'); return; }
    if (role !== 'webmaster') { navigate('/staff/dashboard'); return; }
    fetchConfig();
  }, [navigate]);

  const fetchConfig = async () => {
    try {
      const res = await api.get('/invoice-config');
      setActiveTemplate(res.data.template || 'classique');
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async (templateId) => {
    setSaving(true);
    try {
      await api.put('/invoice-config', { template: templateId });
      setActiveTemplate(templateId);
      toast({ title: 'Template mis a jour', description: `Le template "${TEMPLATES.find(t => t.id === templateId)?.name}" est maintenant actif` });
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder le template', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white flex items-center justify-center">
        <p className="text-gray-400">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f1ed] to-white">
      {/* Header */}
      <div className="bg-[#3d2817] text-white py-4 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <FileText size={22} />
            <div>
              <h1 className="text-xl font-bold" data-testid="invoice-templates-title">Templates de Facture</h1>
              <p className="text-xs text-gray-300">Choisissez le design de vos factures PDF</p>
            </div>
          </div>
          <Button onClick={() => navigate('/staff/dashboard')} size="sm" className="bg-red-600 hover:bg-red-700 text-xs" data-testid="back-dashboard-btn">
            <ArrowLeft size={14} className="mr-1" /> Dashboard
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="mb-4 flex items-center gap-2">
          <p className="text-sm text-gray-600">
            Template actif : <span className="font-bold text-[#2c1810]">{TEMPLATES.find(t => t.id === activeTemplate)?.name}</span>
          </p>
          {saving && <span className="text-xs text-orange-500 animate-pulse">Sauvegarde...</span>}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="templates-grid">
          {TEMPLATES.map(tpl => (
            <TemplatePreview
              key={tpl.id}
              tpl={tpl}
              isActive={activeTemplate === tpl.id}
              onSelect={() => handleSelect(tpl.id)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default InvoiceTemplates;
